import type { ReactNode } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { cx } from "shared/utils";
import gradientBg from "../assets/gradient-bg.jpg";
import { FilterIcon, StarIcon } from "@hugeicons-pro/core-solid-rounded";

interface PreviewFrameProps {
  children: ReactNode;
  innerClassName?: string;
  /** Frame height. Most paywalls use 329; comms uses 363. */
  height?: number;
  /** Skip the auto-centered white card; render children directly. */
  noInnerCard?: boolean;
  /**
   * Chip in the top-left corner. "crm" is the BoomCRM chip every Core-plan
   * upsell wears; "pro" is the BoomCRM Pro chip the Pro-only surfaces use
   * (AI Leasing agent, email channel — Figma BoomCRM-Paywall).
   */
  badge?: "crm" | "pro";
  /**
   * The 399×190 frame of the compact paywall card (Figma 31:23290 / 394:7658):
   * chip centred at the top with a white hairline, gradient anchored top-left,
   * a faint pink fade over the bottom edge. Children position themselves
   * absolutely, as with `noInnerCard`.
   */
  compact?: boolean;
}

/**
 * Pink gradient teaser frame from Figma BoomCRM-Paywall (node 31:19886).
 * 634×329 outer (or custom height) with raster gradient bg matching Figma
 * asset. BoomCRM pill chip top-left. Optionally auto-wraps children in a
 * centered white card (583×237) for table-style previews.
 */
export const PreviewFrame = ({
  children,
  innerClassName,
  height = 329,
  noInnerCard = false,
  badge = "crm",
  compact = false
}: PreviewFrameProps) => {
  // Full-size frames either take the children as-is or centre them in the
  // white card; the compact frame always takes them as-is and adds the fade.
  const fullSizeBody = noInnerCard ? (
    children
  ) : (
    <div
      className={cx(
        "absolute left-1/2 top-[calc(50%+20px)] flex h-[237px] w-[583px] -translate-x-1/2 -translate-y-1/2 items-start overflow-hidden rounded-lg bg-white",
        innerClassName
      )}
      style={{
        boxShadow:
          "0px 12px 16px -4px rgba(28,29,34,0.08), 0px 4px 6px -2px rgba(28,29,34,0.03)"
      }}
    >
      {children}
    </div>
  );

  return (
    <div
      className="relative w-full overflow-hidden rounded-lg border border-alpha-100"
      style={{
        height: `${compact ? 190 : height}px`,
        backgroundImage: `url(${gradientBg})`,
        backgroundSize: compact ? "255.68% 226.49%" : "255.68% 223.04%",
        backgroundPosition: compact ? "0% 0%" : "4% 15%",
        backgroundRepeat: "no-repeat"
      }}
    >
      <div
        className={cx(
          "absolute z-10 flex items-center gap-2 rounded-full border py-1 pl-2 pr-3 text-[13px] font-bold leading-[1.5] text-white",
          compact
            ? "left-1/2 top-[15px] -translate-x-1/2 border-white"
            : "left-[24px] top-[19px] border-transparent"
        )}
        style={{
          background:
            "linear-gradient(270deg, #000 17.08%, #666 117.49%) padding-box, linear-gradient(90deg, rgba(255, 255, 255, 0.5)  0%,  rgba(153, 153, 153, 0.5) 100%) border-box",
          filter: "drop-shadow(0 4px 2px rgba(0,0,0,0.15))"
        }}
      >
        <HugeiconsIcon
          icon={badge === "pro" ? StarIcon : FilterIcon}
          size={compact ? 16 : 14}
          aria-hidden="true"
        />
        {badge === "pro" ? "BoomCRM Pro" : "BoomCRM"}
      </div>
      {compact ? (
        <>
          {children}
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[50px] opacity-[0.12] backdrop-blur-[2px]"
            style={{
              backgroundImage:
                "linear-gradient(180deg, rgba(231,1,127,0) 17.57%, #e7017f 100%)"
            }}
            aria-hidden="true"
          />
        </>
      ) : (
        fullSizeBody
      )}
    </div>
  );
};
